import { useState, useRef, useEffect } from 'react';
import { Configure, Index, useSearchBox } from 'react-instantsearch';
import { Formik } from 'formik';
import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';

import useCollapseRefinements from '@klv/hooks/useCollapseRefinements';
import useStorage from '@klv/hooks/useStorage';
import FilterAccordion from '@klv/atoms/filter-accordion';
import { RadioGroup } from '@klv/molecules/form-field/radio-group';
import Toggle from '@klv/molecules/toggle';
import UnifiedSearchInput from '@klv/molecules/unified-search-input';
import UnifiedSearchResults from '@klv/molecules/unified-search-results';
import { UnifiedSearchNoResults } from '@klv/molecules/unified-search-no-results';

import RefinementsPortal from './refinements-portal';
import SearchFilterLayout from '../search-filter-layout';
import UnifiedSearchResultCount from './unified-search-count';
import useUnifiedSearchIndexes from './hooks/use-unified-search-indexes';

import { clearAllQueryParams, getResourceFromParams, getSelectParamValues, getUrlAndParams, updateResourceQueryParam } from './utils';

import * as s from './styles/unified-search.module.css';

import type { TSearchIndexes } from '@klv/molecules/unified-search-results/types';
import type { IUnifiedSearchProps } from './types';

const UnifiedSearchForm = ({
  backgroundColor = 'light',
  defaultViewType = 'list',
  defaultIndex = '',
  userCourseData,
  viewedResources,
}: IUnifiedSearchProps) => {
  const { t } = useTranslation();
  const searchBox = useRef<HTMLInputElement>(null);
  const { refine } = useSearchBox();

  const resourceParam = getResourceFromParams();

  // default resource filter
  const [resource, setResource] = useState(resourceParam ?? defaultIndex);

  // Do we show a result list or a result grid?
  const [displayAs, setDisplayAs] = useStorage('unified-search-display-as', defaultViewType);
  
  // the algolia indexes to search
  const indexes = useUnifiedSearchIndexes(defaultIndex);
  
  const collapseRefinements = useCollapseRefinements();

  const selectParamValues = getSelectParamValues(getUrlAndParams().params, 'string');

  useEffect(() => {
    // sync the resource state with the initial resource search param
    updateResourceQueryParam(resourceParam ?? defaultIndex);
    setResource(resourceParam ?? defaultIndex);

    // sync the resource state with active history entry
    const updateResource = () => {
      setResource(getResourceFromParams() ?? '');
    };

    window.addEventListener('popstate', updateResource);

    return () => {
      window.removeEventListener('popstate', updateResource);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SearchFilterLayout
      headline={t('pages_search_headline')}
      search={<UnifiedSearchInput backgroundColor={backgroundColor} searchBox={searchBox} />}
      filters={(<>
        <div className={s.accordionToggles}>
          <button 
            className={s.toggleButton} 
            type="button" 
            onClick={collapseRefinements}>
              {t('pages_search_filters_collapseAll')}
          </button>
          <button 
            className={s.toggleButton}
            type="button"
            onClick={() => {
              setResource('');
              refine('');
              clearAllQueryParams();
              if (searchBox.current) searchBox.current.value = '';
            }}>
              {t('pages_search_filters_clearAll')}
          </button>
        </div>
        <Formik enableReinitialize={true} initialValues={{ 'resource': resource }} onSubmit={(bag) => {
          setResource(bag.resource);
          // update the URL with the selected resource
          updateResourceQueryParam(bag.resource, true);
        }}>
          {props => (
            <form onChange={props.handleSubmit}>
              <FilterAccordion label={t('pages_search_filters_resource')} className="includeDesktop">
                <RadioGroup 
                  name="resource" 
                  className={s.resourceMenu}
                  options={[{
                    label: t('pages_search_filters_allResources'),
                    value: '',
                  }, ...indexes]} 
                  backgroundColor={backgroundColor}
                />
              </FilterAccordion>
            </form>
          )}
        </Formik>
        {/* This is our container that will catch our refinement lists using a react portal */}
        <div id="search-filters-portal" className="refinements"></div>
      </>)}
      results={(
        <>
          <div className={s.resultToggleBar}>
            {/* This is our container that will catch our results count a react portal */}
            <div id="unified-search-result-count"></div>
            <Toggle
              backgroundColor={backgroundColor}
              trueIcon='List'
              falseIcon='SquareGridOutline'
              setValue={(value: boolean) => setDisplayAs(value ? 'list' : 'grid')}
              value={displayAs === 'list'}
              label={t('pages_search_toggleLabel')}
            />
          </div>
          {indexes.map((index, i) => {
            // if the index value matches resource or resource is empty, show the index
            if (index.value === resource || resource === '') {
              
              // define hits per page
              let hitsPerPage: number;
              
              //if a resource is selected, show more results
              if (resource) {
                hitsPerPage = displayAs === 'list' ? 10 : 15;
              } else {
                // if a resource isn't selected, show fewer results
                hitsPerPage = displayAs === 'list' ? 3 : 6;
              }
              return (
                <Index
                  indexName={index.value}
                  key={index.value}
                >
                  <Configure hitsPerPage={hitsPerPage} filters={index.filters} />
                  <RefinementsPortal key={selectParamValues as string} backgroundColor={backgroundColor} resource={resource} />
                  {/**
                   * This has to be nested under an Index tag or we will get an infinite loop 
                   * We only want one shown though so if resource is empty and we are on the first index, show the count
                   * If resource is not empty, show the count for the selected index
                   **/}
                  {((resource === '' && i === 0) || index.value === resource )  && (
                    <>
                      <UnifiedSearchResultCount resource={resource as TSearchIndexes}/>
                      <UnifiedSearchNoResults />
                    </>
                  ) }
                  <UnifiedSearchResults
                    backgroundColor={backgroundColor}
                    defaultIndex={defaultIndex}
                    index={index.value}
                    selectedIndex={resource}
                    setSelectedIndex={setResource}
                    title={index.label}
                    type={displayAs}
                    userCourseData={userCourseData}
                    viewedResources={viewedResources}
                  />
                </Index>
              );
            }
          })}
        </>
      )}
    />
  );
};

export default UnifiedSearchForm;
